/**
 * PropertyCheckDetail — single Property Check view.
 *
 * Mounted at /property-check/c/:id. Shows status, the request details, the
 * delivered report (when available), and a cancel button while still cancellable.
 *
 * Uses VerifierMatchingStatus when the check is in pending/awaiting state
 * for live updates; falls back to a static detail view otherwise.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatIcon from '@mui/icons-material/Chat';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import propertyCheckService from '../services/propertyCheckService';
import VerifierMatchingStatus from '../components/property-check/VerifierMatchingStatus';
import { colors } from '../theme/colors';

const formatStatus = (status) => String(status || 'pending').replace(/_/g, ' ');

const DetailMetric = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      border: `1px solid ${colors.border.light}`,
      borderRadius: 2,
      bgcolor: colors.bg.paper,
      minHeight: 94,
    }}
  >
    <Typography
      variant="overline"
      sx={{
        color: colors.text.secondary,
        letterSpacing: '0.12em',
        lineHeight: 1.4,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5, color: colors.text.primary, fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

const PropertyCheckDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoZoom, setPhotoZoom] = useState(1);
  const confirmAttemptedRef = useRef(new Set());

  const reload = async () => {
    setLoading(true);
    try {
      const fresh = await propertyCheckService.get(id);
      setCheck(fresh);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const paymentPending = ['pending_payment', 'pending'].includes(check?.payment?.status);
    if (
      !check?.id ||
      !paymentPending ||
      check.payment?.method !== 'stripe_checkout' ||
      confirmingPayment ||
      confirmAttemptedRef.current.has(check.id)
    ) {
      return undefined;
    }

    let cancelled = false;
    confirmAttemptedRef.current.add(check.id);
    setConfirmingPayment(true);
    propertyCheckService.confirmStripeCheckout({ requestId: check.id })
      .then((result) => {
        if (!cancelled && result.propertyCheck) {
          setCheck(result.propertyCheck);
        }
      })
      .catch(() => {
        // Keep the visible pending-payment message; the success page or webhook
        // may still confirm shortly.
      })
      .finally(() => {
        if (!cancelled) setConfirmingPayment(false);
      });

    return () => {
      cancelled = true;
    };
  }, [check?.id, check?.payment?.method, check?.payment?.status, confirmingPayment]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await propertyCheckService.cancel(id);
      setCancelResult(res);
      setCancelOpen(false);
      await reload();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
      setCancelOpen(false);
    } finally {
      setCancelling(false);
    }
  };

  const openPhoto = (photo) => {
    setSelectedPhoto(photo);
    setPhotoZoom(1);
  };

  if (loading && !check) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !check) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/property-check/dashboard')}>Back to dashboard</Button>
      </Container>
    );
  }

  if (!check) return null;

  const paymentPending = ['pending_payment', 'pending'].includes(check.payment?.status);
  const payOnReport = check.payment?.status === 'deferred';
  const isLive = check.status === 'pending' && !paymentPending;
  const isAssigned = check.status === 'assigned';
  const isCancelled = check.status === 'cancelled';
  const isComplete = ['verification_completed', 'approved', 'completed'].includes(check.status);
  const canCancel = ['pending', 'assigned'].includes(check.status);
  const reportProcessing = ['pending', 'in_progress'].includes(check.aiEnrichment?.status);
  const address = check.location?.address ||
    check.location?.formattedAddress ||
    (typeof check.location === 'string' ? check.location : 'Address not specified');
  const scheduleText = check.scheduling?.flexibility === 'specific_date'
    ? check.scheduling.requestedDate
    : check.scheduling?.flexibility === 'verifier_proposes'
    ? 'Verifier proposes a time'
    : 'This week';
  const paymentText = payOnReport
    ? `$${Number(check.payment?.amount || 0).toFixed(2)} · pay when the report arrives`
    : `$${Number(check.payment?.amount || 0).toFixed(2)} - ${check.payment?.status || 'pending'}${
      check.payment?.method ? ` · ${check.payment.method}` : ''
    }`;
  const payNow = async () => {
    try {
      setPaying(true);
      const r = await propertyCheckService.pay(check.id);
      if (r?.checkoutUrl) window.location.href = r.checkoutUrl;
      else setPaying(false);
    } catch (e) {
      setPaying(false);
      setPayError(e?.response?.data?.error || e.message || 'Could not start payment');
    }
  };
  const reportStandard = check.reportStandard;

  // Refund messaging for cancelled checks. The backend exposes both
  // payment.status (refunded / refund_pending / refund_failed) and
  // payment.refundStatus (completed / succeeded / pending / failed). Reflect
  // the real state instead of always saying "in flight".
  const refundAmount = Number(check.payment?.refundAmount || 0);
  const refundDone = ['refunded'].includes(check.payment?.status) ||
    ['completed', 'succeeded'].includes(check.payment?.refundStatus);
  const refundFailed = check.payment?.status === 'refund_failed' ||
    check.payment?.refundStatus === 'failed';
  let refundMessage = null;
  let refundSeverity = 'warning';
  if (refundAmount > 0) {
    if (refundFailed) {
      refundMessage = `We hit a snag issuing your $${refundAmount.toFixed(2)} refund. Our team has been notified and will resolve it — no action needed on your part.`;
      refundSeverity = 'error';
    } else if (refundDone) {
      refundMessage = `A refund of $${refundAmount.toFixed(2)} has been issued to your original payment method. It typically settles within 1–3 business days.`;
      refundSeverity = 'success';
    } else {
      refundMessage = `Refund of $${refundAmount.toFixed(2)} is being processed and will return to your original payment method within 1–3 business days.`;
    }
  }
  const reportMeetsStandard = !reportStandard || reportStandard.status === 'professional';
  const evidencePhotos = (check.evidenceAssets?.photos || []).filter((photo) => photo.url);
  const missingDeliverables = (check.deliverables || []).filter((item) =>
    ['missing', 'short', 'partial'].includes(item.status)
  );
  const reportPanelColor = reportProcessing
    ? colors.status.info
    : reportMeetsStandard
    ? colors.status.success
    : colors.status.warning;
  const reportPanelBg = reportProcessing
    ? colors.status.infoBg
    : reportMeetsStandard
    ? colors.status.successBg
    : colors.status.warningBg;
  const statusLabel = isComplete && !reportMeetsStandard
    ? 'incomplete report'
    : formatStatus(check.status);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/property-check/dashboard')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to dashboard
      </Button>

      {cancelResult && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Cancelled. {cancelResult.refund?.percentage > 0
            ? `Refund of $${cancelResult.refund.amount.toFixed(2)} (${cancelResult.refund.percentage}%) is being processed — funds usually return in 1–3 business days.`
            : 'No refund applies per the cancellation policy.'}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 4,
          border: `1px solid ${colors.border.light}`,
          boxShadow: colors.shadow.md,
          bgcolor: colors.bg.paper,
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            color: colors.text.inverse,
            background: `linear-gradient(135deg, ${colors.brand.primaryDeep} 0%, ${colors.brand.primary} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
            spacing={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.78, letterSpacing: '0.12em' }}>
                {check.id}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mt: 0.5,
                  mb: 1.5,
                  fontSize: { xs: '2rem', md: '3rem' },
                  lineHeight: 1.05,
                  overflowWrap: 'anywhere',
                }}
              >
                {check.package?.displayName || 'Property Check'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.86, maxWidth: 760 }}>
                {address}
              </Typography>
            </Box>
            <Chip
              label={statusLabel}
              sx={{
                bgcolor: isComplete && !reportMeetsStandard
                  ? alpha(colors.status.warning, 0.24)
                  : alpha('#ffffff', 0.16),
                color: colors.text.inverse,
                border: `1px solid ${alpha('#ffffff', 0.38)}`,
                fontWeight: 700,
                textTransform: 'capitalize',
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <DetailMetric label="Schedule" value={scheduleText} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailMetric label="Payment" value={paymentText} />
            </Grid>
          </Grid>

        {paymentPending && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info">
              {confirmingPayment
                ? 'Confirming your Stripe payment now...'
                : 'We are still confirming payment for this verification. Verifier matching will start automatically once the payment is confirmed.'}
            </Alert>
          </Box>
        )}

        {isLive && (
          <Box sx={{ mb: 3 }}>
            <VerifierMatchingStatus requestId={check.id} onAssigned={reload} onCancelled={reload} />
          </Box>
        )}

        {isAssigned && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info">
              A verifier has accepted your check. They'll reach out shortly.
            </Alert>
          </Box>
        )}

        {isComplete && check.reportLocked && (
          <Box sx={{ mb: 3, p: { xs: 2.5, md: 3 }, borderRadius: 3, bgcolor: colors.status.successBg, border: `1px solid ${alpha(colors.status.success, 0.24)}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Your report is ready</Typography>
            <Typography variant="body1" sx={{ mt: 0.5, color: colors.text.primary }}>
              The verifier has visited and sent in dated, GPS-tagged photos and a written report. Pay the ${Number(check.payment?.amount || 0).toFixed(0)} fee to open it. Nothing was charged before the visit.
            </Typography>
            {payError && <Alert severity="error" sx={{ mt: 1.5 }}>{payError}</Alert>}
            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={payNow} disabled={paying}>
                {paying ? 'Opening secure checkout…' : `Pay $${Number(check.payment?.amount || 0).toFixed(0)} and open the report`}
              </Button>
            </Stack>
          </Box>
        )}
        {isComplete && !check.reportLocked && (
          <Box
            sx={{
              mb: 3,
              p: { xs: 2.5, md: 3 },
              borderRadius: 3,
              bgcolor: reportPanelBg,
              border: `1px solid ${alpha(reportPanelColor, 0.24)}`,
            }}
          >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {reportProcessing
                  ? 'Report being prepared'
                  : reportMeetsStandard
                  ? 'Report ready'
                  : 'Incomplete report: verifier did not meet package standard'}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, color: colors.text.primary }}>
                {reportProcessing
                  ? 'Your verifier completed the visit. Eqaya is analyzing the photos and preparing the final report.'
                  : reportMeetsStandard
                  ? 'Your verifier completed the visit. You can download the photo-backed report now.'
                  : `Your verifier completed the visit, but this report scored ${reportStandard?.score ?? 'below standard'}/100 and is missing required package evidence.`}
              </Typography>
              {!reportProcessing && !reportMeetsStandard && missingDeliverables.length > 0 && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  {missingDeliverables.slice(0, 4).map((item) => (
                    <Chip
                      key={item.id || item.label}
                      size="small"
                      label={`${formatStatus(item.status)}: ${item.label}`}
                      sx={{
                        bgcolor: colors.bg.paper,
                        color: colors.status.warningDark,
                        border: `1px solid ${alpha(colors.status.warning, 0.28)}`,
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
              )}
              {!reportProcessing && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setReportOpen(true)}
                  >
                    View report
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    href={propertyCheckService.reportUrl(check.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download report
                  </Button>
                </Stack>
              )}
          </Box>
        )}

        {isComplete && !check.reportLocked && evidencePhotos.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Submitted photos
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  View or download the images used in this Property Check report.
                </Typography>
              </Box>
              <Chip size="small" label={`${evidencePhotos.length} photos`} />
            </Stack>
            <Grid container spacing={2}>
              {evidencePhotos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} key={photo.id || `${photo.url}-${index}`}>
                  <Box
                    sx={{
                      border: `1px solid ${colors.border.light}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: colors.bg.paper,
                      height: '100%',
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => openPhoto(photo)}
                      sx={{
                        display: 'block',
                        width: '100%',
                        p: 0,
                        border: 0,
                        cursor: 'zoom-in',
                        bgcolor: colors.bg.subtle,
                        aspectRatio: '4 / 3',
                      }}
                    >
                      <Box
                        component="img"
                        src={photo.url}
                        alt={photo.caption || photo.label || `verification photo ${index + 1}`}
                        loading="lazy"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {photo.label || `Photo ${index + 1}`}
                        {photo.room ? ` · ${photo.room}` : ''}
                      </Typography>
                      {photo.caption && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.text.secondary,
                            mt: 0.5,
                            minHeight: 40,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {photo.caption}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                        onClick={() => openPhoto(photo)}
                      >
                        View
                      </Button>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<DownloadIcon />}
                          href={photo.downloadUrl || photo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Download
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {isCancelled && (
          <Box sx={{ mb: 3 }}>
            <Alert severity={refundMessage ? refundSeverity : 'warning'}>
              This verification was cancelled.
              {refundMessage
                ? ` ${refundMessage}`
                : ' No refund applies per the cancellation policy.'}
            </Alert>
          </Box>
        )}

        <Grid container spacing={3}>
          {check.description && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 3,
                  bgcolor: colors.bg.subtle,
                  border: `1px solid ${colors.border.light}`,
                }}
              >
                <Typography variant="overline" sx={{ color: colors.text.secondary, letterSpacing: '0.12em' }}>
                  What the verifier was asked to check
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1,
                    whiteSpace: 'pre-line',
                    color: colors.text.primary,
                    lineHeight: 1.75,
                  }}
                >
                  {check.description}
                </Typography>
              </Box>
            </Grid>
          )}
          {check.focusAreas?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary">
                Focus areas
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {check.focusAreas.map((area) => (
                  <Chip key={area} label={area} size="small" variant="outlined" />
                ))}
              </Stack>
            </Grid>
          )}
          {check.propertyAccess && Object.values(check.propertyAccess).some((v) => v) && (
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary">
                Property access
              </Typography>
              <List dense disablePadding>
                {check.propertyAccess.whoIsThere && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`Who's at the property: ${check.propertyAccess.whoIsThere}`}
                    />
                  </ListItem>
                )}
                {check.propertyAccess.contactName && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`Contact: ${check.propertyAccess.contactName}${
                        check.propertyAccess.contactPhone
                          ? ` (${check.propertyAccess.contactPhone})`
                          : ''
                      }`}
                    />
                  </ListItem>
                )}
                {check.propertyAccess.accessInstructions && (
                  <ListItem disableGutters>
                    <ListItemText primary={check.propertyAccess.accessInstructions} />
                  </ListItem>
                )}
                {check.propertyAccess.viewingWindow && (
                  <ListItem disableGutters>
                    <ListItemText primary={`Window: ${check.propertyAccess.viewingWindow}`} />
                  </ListItem>
                )}
              </List>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {check.verifierId && (
            <Button
              variant="outlined"
              startIcon={<ChatIcon />}
              onClick={() => navigate(`/messages?conversation=&verifierId=${check.verifierId}`)}
            >
              Message verifier
            </Button>
          )}
          {canCancel && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => setCancelOpen(true)}
            >
              Cancel this check
            </Button>
          )}
        </Stack>
        </Box>
      </Paper>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Cancel this verification?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Refund policy:
            <ul>
              <li>Before a verifier accepts: full refund.</li>
              <li>Within 2h of a verifier accepting: 50% refund.</li>
              <li>After 2h: no refund.</li>
            </ul>
            Mobile-money refunds typically settle in 1–3 business days.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep booking</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={20} /> : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Property Check report
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<PictureAsPdfIcon />}
              href={check ? propertyCheckService.reportUrl(check.id) : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </Button>
            <IconButton onClick={() => setReportOpen(false)} aria-label="Close report">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {check && reportOpen && (
            <Box
              component="iframe"
              title="Property Check report"
              src={propertyCheckService.reportViewUrl(check.id)}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: '#0b0f0f' } }}
      >
        {selectedPhoto && (
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', color: '#fff' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{
                px: { xs: 1, md: 2 },
                py: 1,
                borderBottom: `1px solid ${alpha('#ffffff', 0.14)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                {selectedPhoto.label || 'Submitted photo'}
                {selectedPhoto.room ? ` · ${selectedPhoto.room}` : ''}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  aria-label="Zoom out"
                  onClick={() => setPhotoZoom((z) => Math.max(1, Number((z - 0.5).toFixed(1))))}
                  sx={{ color: '#fff' }}
                >
                  <ZoomOutIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 42, textAlign: 'center' }}>
                  {Math.round(photoZoom * 100)}%
                </Typography>
                <IconButton
                  aria-label="Zoom in"
                  onClick={() => setPhotoZoom((z) => Math.min(5, Number((z + 0.5).toFixed(1))))}
                  sx={{ color: '#fff' }}
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton
                  aria-label="Reset zoom"
                  onClick={() => setPhotoZoom(1)}
                  sx={{ color: '#fff' }}
                >
                  <RestartAltIcon />
                </IconButton>
                <IconButton
                  aria-label="Download photo"
                  component="a"
                  href={selectedPhoto.downloadUrl || selectedPhoto.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  sx={{ color: '#fff' }}
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  aria-label="Close photo viewer"
                  onClick={() => setSelectedPhoto(null)}
                  sx={{ color: '#fff' }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' },
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  overflow: 'auto',
                  display: 'flex',
                  alignItems: photoZoom === 1 ? 'center' : 'flex-start',
                  justifyContent: photoZoom === 1 ? 'center' : 'flex-start',
                  p: { xs: 1, md: 2 },
                  touchAction: 'pan-x pan-y',
                }}
              >
                <Box
                  component="img"
                  src={selectedPhoto.url}
                  alt={selectedPhoto.caption || selectedPhoto.label || 'Submitted property photo'}
                  sx={{
                    display: 'block',
                    width: photoZoom === 1 ? 'auto' : `${photoZoom * 100}%`,
                    maxWidth: photoZoom === 1 ? '100%' : 'none',
                    maxHeight: photoZoom === 1 ? '100%' : 'none',
                    objectFit: 'contain',
                    imageRendering: 'auto',
                  }}
                />
              </Box>

              <Box
                sx={{
                  borderTop: { xs: `1px solid ${alpha('#ffffff', 0.14)}`, md: 0 },
                  borderLeft: { xs: 0, md: `1px solid ${alpha('#ffffff', 0.14)}` },
                  p: { xs: 2, md: 3 },
                  overflowY: 'auto',
                  bgcolor: alpha('#ffffff', 0.04),
                }}
              >
                <Typography variant="overline" sx={{ color: alpha('#ffffff', 0.62), letterSpacing: '0.12em' }}>
                  Comment
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.65 }}>
                  {selectedPhoto.comment || selectedPhoto.caption || 'No comment was provided for this photo.'}
                </Typography>
                {selectedPhoto.caption && selectedPhoto.comment && (
                  <>
                    <Typography variant="overline" sx={{ display: 'block', mt: 3, color: alpha('#ffffff', 0.62), letterSpacing: '0.12em' }}>
                      Caption
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: alpha('#ffffff', 0.78), lineHeight: 1.6 }}>
                      {selectedPhoto.caption}
                    </Typography>
                  </>
                )}
                {selectedPhoto.issues?.length > 0 && (
                  <>
                    <Typography variant="overline" sx={{ display: 'block', mt: 3, color: alpha('#ffffff', 0.62), letterSpacing: '0.12em' }}>
                      Flagged issues
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {selectedPhoto.issues.map((issue, index) => (
                        <Chip
                          key={`${typeof issue === 'string' ? issue : issue?.description}-${index}`}
                          label={typeof issue === 'string' ? issue : issue?.description || 'Issue flagged'}
                          sx={{
                            justifyContent: 'flex-start',
                            color: '#fff',
                            bgcolor: alpha(colors.status.warning, 0.22),
                            border: `1px solid ${alpha(colors.status.warning, 0.42)}`,
                            '& .MuiChip-label': {
                              whiteSpace: 'normal',
                              py: 0.75,
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </Container>
  );
};

export default PropertyCheckDetail;
